import React from "react";
import dayjs from "dayjs";
import {
    Box, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useColorModeValue,
} from "@chakra-ui/react";
import {
    RESERVATION_STATUS_LABELS, RESERVATION_STATUS_COLORS, DEFAULT_CHIP_COLOR, REFUND_MAX_RETRY,
} from "./paymentsConstants";
import PaymentProviderChip from "./PaymentProviderChip";
import SchemeBadge from "./SchemeBadge";

/**
 * Renders a colored badge for a reservation status.
 * @param {Object} props - { status }
 * @returns {React.ReactElement}
 */
function ReservationStatusChip({ status }) {
    const scheme = RESERVATION_STATUS_COLORS[status] || DEFAULT_CHIP_COLOR;
    const label = RESERVATION_STATUS_LABELS[status] || status || "—";
    return <SchemeBadge scheme={scheme} label={label} />;
}

/**
 * Renders an attempts counter with red highlight when at the cap.
 * @param {Object} props - { count }
 * @returns {React.ReactElement}
 */
function AttemptsCounter({ count }) {
    const value = count ?? 0;
    const danger = value >= REFUND_MAX_RETRY;
    const scheme = danger ? RESERVATION_STATUS_COLORS.refund_manual_required : DEFAULT_CHIP_COLOR;
    return <SchemeBadge scheme={scheme} label={`${value} / ${REFUND_MAX_RETRY}`} />;
}

/**
 * Formats a numeric amount with currency code (2 decimals).
 * @param {number|null|undefined} amount - Amount value
 * @param {string} currency - Currency code
 * @returns {string}
 */
function formatAmount(amount, currency) {
    if (amount === null || amount === undefined) return "—";
    const upper = (currency || "").toUpperCase();
    return `${Number(amount).toFixed(2)} ${upper}`.trim();
}

/**
 * Formats a date string as DD/MM/YYYY HH:mm or "—".
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDateTime(value) {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Renders the last-error compact cell with tooltip on the full message.
 * @param {Object} props - { attempt }
 * @returns {React.ReactElement}
 */
function LastErrorCell({ attempt }) {
    if (!attempt) return <Text as="span" opacity="0.5">—</Text>;
    const code = attempt.error_code || "—";
    const message = attempt.error_message || "Échec sans message";
    return (
        <Tooltip label={message} placement="top" hasArrow>
            <Text as="span" fontSize="0.75rem" fontFamily="mono" color="red.400" cursor="help">{code}</Text>
        </Tooltip>
    );
}

/**
 * Renders the table head row.
 * @returns {React.ReactElement}
 */
function RefundsHead() {
    return (
        <Thead>
            <Tr>
                <Th>Paiement</Th><Th>Réservation</Th><Th>Canal</Th><Th>Montant</Th>
                <Th>Tentatives</Th><Th>Dernière erreur</Th><Th>Statut</Th><Th>Mis à jour</Th>
            </Tr>
        </Thead>
    );
}

/**
 * Renders the payment id + reference cell.
 * @param {Object} props - { payment }
 * @returns {React.ReactElement}
 */
function PaymentRefCell({ payment }) {
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            <Text fontFamily="mono" fontSize="0.78rem">#{payment.id}</Text>
            <Text fontSize="0.7rem" color={subColor}>{payment.reference || payment.payment_intent_id || "—"}</Text>
        </Box>
    );
}

/**
 * Renders a single refund row.
 * @param {Object} props - { payment, onSelect, hoverBg }
 * @returns {React.ReactElement}
 */
function RefundRow({ payment, onSelect, hoverBg }) {
    return (
        <Tr cursor={onSelect ? "pointer" : "default"} _hover={{ bg: hoverBg }} onClick={() => onSelect?.(payment.id)}>
            <Td><PaymentRefCell payment={payment} /></Td>
            <Td>{payment.reservation_id ? `#${payment.reservation_id}` : "—"}</Td>
            <Td><PaymentProviderChip payment={payment} /></Td>
            <Td>{formatAmount(payment.total_amount, "")}</Td>
            <Td><AttemptsCounter count={payment.refund_attempts} /></Td>
            <Td><LastErrorCell attempt={payment.last_refund_attempt} /></Td>
            <Td><ReservationStatusChip status={payment.reservation_status} /></Td>
            <Td>{formatDateTime(payment.last_refund_attempt?.created_at || payment.modified_at)}</Td>
        </Tr>
    );
}

/**
 * Renders the refunds table or an empty placeholder.
 * @param {Object} props - { refunds, onSelect }
 * @returns {React.ReactElement}
 */
function RefundsPendingTable({ refunds, onSelect }) {
    const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (!refunds || refunds.length === 0) {
        return <Text color={emptyColor} fontSize="sm" textAlign="center" py="32px">Aucun remboursement en attente d'action manuelle</Text>;
    }
    return (
        <TableContainer maxH="500px" overflowY="auto">
            <Table size="sm">
                <RefundsHead />
                <Tbody>
                    {refunds.map((p) => <RefundRow key={p.id} payment={p} onSelect={onSelect} hoverBg={hoverBg} />)}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

export default RefundsPendingTable;
